import React, { useCallback, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import styled from "styled-components";
import heicConvert from "heic-convert";

import { useNotification } from "@src/context/NotificationContext";

// Register a Divider blot (<hr>) so we can insert an "article spacer" from the toolbar.
const registerDividerBlot = (() => {
  let registered = false;
  return () => {
    if (registered) return;
    try {
      const Quill = (ReactQuill as any).Quill;
      if (!Quill) return;
      const BlockEmbed = Quill.import("blots/block/embed");
      class DividerBlot extends BlockEmbed {
        static blotName = "divider";
        static tagName = "hr";
      }
      Quill.register(DividerBlot, true);
      registered = true;
    } catch {
      // no-op (editor still works with default formatting)
    }
  };
})();

const EditorContainer = styled.div`
  width: 100%;
  max-width: 100%;
  background-color: ${({ theme }): string => theme.colors.darkBg};
  box-sizing: border-box;
  position: relative;

  .ql-container {
    font-size: 16px;
    color: #979794;
    background-color: ${({ theme }): string => theme.colors.darkBg};
    border: none;
    min-height: 400px;
    font-family: inherit;
    width: 100%;
  }

  .ql-container.ql-snow {
    border: none;
  }

  .ql-editor {
    min-height: 400px;
    padding: ${({ theme }): string => theme.margins.medium};
    color: #979794 !important;
    text-align: left;
    box-sizing: border-box;
    word-wrap: break-word;
    overflow-wrap: break-word;
    font-size: 16px !important;
    line-height: 1.6;
    width: 100%;
    background-color: ${({ theme }): string => theme.colors.darkGray};
    border-radius: 0px 0px 10px 10px;
  }

  .ql-editor p {
    font-size: 16px !important;
    line-height: 1.6;
    margin: 0.5em 0;
    color: #979794;
  }

  .ql-editor p,
  .ql-editor ol,
  .ql-editor ul,
  .ql-editor pre,
  .ql-editor blockquote,
  .ql-editor h1,
  .ql-editor h2 {
    color: #979794;
  }

  .ql-editor.ql-blank::before {
    color: ${({ theme }): string => theme.colors.gray};
    font-style: normal;
    left: ${({ theme }): string => theme.margins.medium};
  }

  .ql-toolbar {
    background-color: ${({ theme }): string => theme.colors.darkBg};
    border: none;
    border-bottom: 1px solid ${({ theme }): string => theme.colors.gray};
    padding: ${({ theme }): string => theme.margins.medium};
    box-sizing: border-box;
    /* Keep toolbar accessible while scrolling the article body */
    position: sticky;
    top: -40px;
    z-index: 10;
    /* Ensure solid background to prevent content showing through */
    backdrop-filter: none;
    /* Shadow when floating (sticky) */
    box-shadow:
      0 4px 6px -1px rgba(0, 0, 0, 0.3),
      0 2px 4px -1px rgba(0, 0, 0, 0.2);
  }

  .ql-toolbar-custom-right {
    position: absolute;
    right: ${({ theme }): string => theme.margins.medium};
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    z-index: 11;
  }

  .ql-toolbar.ql-snow {
    border: none;
    border-bottom: 1px solid ${({ theme }): string => theme.colors.gray};
  }

  .ql-toolbar .ql-stroke {
    stroke: ${({ theme }): string => theme.colors.yellow};
  }

  .ql-toolbar .ql-fill {
    fill: ${({ theme }): string => theme.colors.yellow};
  }

  .ql-toolbar .ql-picker-label {
    color: ${({ theme }): string => theme.colors.yellow};
  }

  /* Remove Quill default blue hover/active styles on picker */
  .ql-snow .ql-picker-label:hover,
  .ql-snow .ql-picker-label.ql-active,
  .ql-snow .ql-picker.ql-expanded .ql-picker-label {
    color: ${({ theme }): string => theme.colors.yellow} !important;
    border-color: ${({ theme }): string => theme.colors.yellow} !important;
  }

  /* Picker chevrons/icons (were still blue in some browsers) */
  .ql-toolbar .ql-picker-label svg,
  .ql-toolbar .ql-picker-label svg .ql-stroke {
    stroke: ${({ theme }): string => theme.colors.yellow} !important;
  }
  .ql-toolbar .ql-picker-label svg .ql-fill {
    fill: ${({ theme }): string => theme.colors.yellow} !important;
  }

  .ql-toolbar .ql-picker-options {
    background-color: ${({ theme }): string => theme.colors.darkBg};
    border: 1px solid ${({ theme }): string => theme.colors.gray};
  }

  .ql-toolbar .ql-picker-item {
    color: ${({ theme }): string => theme.colors.white};
  }

  .ql-toolbar .ql-picker-item:hover {
    color: ${({ theme }): string => theme.colors.yellow};
  }

  .ql-snow .ql-picker-options .ql-picker-item.ql-selected {
    color: ${({ theme }): string => theme.colors.yellow} !important;
  }

  .ql-toolbar button:hover,
  .ql-toolbar button:focus,
  .ql-toolbar button.ql-active {
    color: ${({ theme }): string => theme.colors.yellow};
  }

  .ql-toolbar button:hover .ql-stroke,
  .ql-toolbar button:focus .ql-stroke,
  .ql-toolbar button.ql-active .ql-stroke {
    stroke: ${({ theme }): string => theme.colors.yellow};
  }

  .ql-toolbar button:hover .ql-fill,
  .ql-toolbar button:focus .ql-fill,
  .ql-toolbar button.ql-active .ql-fill {
    fill: ${({ theme }): string => theme.colors.yellow};
  }

  .ql-snow .ql-stroke {
    stroke: ${({ theme }): string => theme.colors.yellow};
  }

  .ql-snow .ql-fill {
    fill: ${({ theme }): string => theme.colors.yellow};
  }

  /* Divider (<hr>) styling inside the editor */
  .ql-editor hr {
    border: 0;
    border-top: 1px solid ${({ theme }): string => theme.colors.gray};
    margin: 24px 0;
  }

  .ql-editor img {
    max-width: 100%;
    border-radius: 12px;
    display: block;
    margin: 16px 0;
  }

  /* Toolbar button icon for custom "divider" */
  .ql-toolbar button.ql-divider {
    width: 28px;
    height: 28px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    border: 1px solid ${({ theme }): string => theme.colors.gray};
  }
  .ql-toolbar button.ql-divider::before {
    content: "—";
    color: ${({ theme }): string => theme.colors.yellow};
    font-size: 16px;
    line-height: 1;
  }
  .ql-toolbar button.ql-divider.ql-active {
    background-color: ${({ theme }): string => theme.colors.yellow};
  }
  .ql-toolbar button.ql-divider.ql-active::before {
    color: ${({ theme }): string => theme.colors.black};
  }
`;

interface RichTextEditorProps {
  value?: string;
  onChange: (html: string) => void;
  placeholder?: string;
  toolbarOnly?: boolean;
  hideToolbar?: boolean;
  /** When true, editor is view-only and the toolbar is hidden. */
  readOnly?: boolean;
  toolbarOnTop?: boolean;
  onUploadImage?: (file: File) => Promise<string>;
  onUploadStateChange?: (uploading: boolean) => void;
  toolbarRightContent?: React.ReactNode;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value = "",
  onChange,
  placeholder = "Write your article here...",
  toolbarOnly = false,
  hideToolbar = false,
  readOnly = false,
  onUploadImage,
  onUploadStateChange,
  toolbarRightContent,
}) => {
  const quillRef = useRef<ReactQuill>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const customContentRootRef = useRef<{
    unmount: () => void;
  } | null>(null);
  const onUploadImageRef =
    useRef<RichTextEditorProps["onUploadImage"]>(onUploadImage);
  const onUploadStateChangeRef =
    useRef<RichTextEditorProps["onUploadStateChange"]>(onUploadStateChange);

  useEffect(() => {
    registerDividerBlot();
  }, []);

  useEffect(() => {
    onUploadImageRef.current = onUploadImage;
  }, [onUploadImage]);

  useEffect(() => {
    onUploadStateChangeRef.current = onUploadStateChange;
  }, [onUploadStateChange]);

  // Insert custom content into toolbar (right side)
  useEffect(() => {
    if (!toolbarRightContent) {
      // Cleanup if content is removed
      if (customContentRootRef.current) {
        customContentRootRef.current.unmount();
        customContentRootRef.current = null;
      }
      return;
    }

    let timeoutId: NodeJS.Timeout | null = null;
    let isMounted = true;

    const setupCustomContent = async (): Promise<void> => {
      if (!isMounted) return;

      // Wait for ReactQuill to be fully mounted
      if (!quillRef.current) {
        timeoutId = setTimeout(setupCustomContent, 100);
        return;
      }

      try {
        const editor = quillRef.current.getEditor();
        const toolbar = editor.root.parentElement?.querySelector(
          ".ql-toolbar"
        ) as HTMLElement;

        if (!toolbar) {
          timeoutId = setTimeout(setupCustomContent, 100);
          return;
        }

        // Remove existing custom content container if it exists
        const existingContainer = toolbar.querySelector(
          ".ql-toolbar-custom-right"
        );
        if (existingContainer) {
          if (customContentRootRef.current) {
            customContentRootRef.current.unmount();
            customContentRootRef.current = null;
          }
          existingContainer.remove();
        }

        // Create container for custom content
        const customContainer = document.createElement("div");
        customContainer.className = "ql-toolbar-custom-right";
        toolbar.appendChild(customContainer);

        // Use React to render the custom content
        const root = document.createElement("div");
        customContainer.appendChild(root);

        // Import ReactDOM dynamically to render the content
        const { createRoot } = await import("react-dom/client");
        const reactRoot = createRoot(root);
        reactRoot.render(toolbarRightContent);
        customContentRootRef.current = reactRoot;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error setting up custom toolbar content:", error);
      }
    };

    // Initial setup with a small delay to ensure ReactQuill is ready
    timeoutId = setTimeout(setupCustomContent, 200);

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (customContentRootRef.current) {
        customContentRootRef.current.unmount();
        customContentRootRef.current = null;
      }
    };
  }, [toolbarRightContent]);

  // NOTE: ReactQuill is controlled via the `value` prop; avoid manual DOM writes
  // to quill.root.innerHTML because it can desync Quill and cause the editor
  // to reset/disappear during typing.

  const insertImageAtCursor = (imageUrl: string) => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    const range = quill.getSelection(true);
    const index = range ? range.index : quill.getLength();
    quill.insertEmbed(index, "image", imageUrl, "user");
    quill.setSelection(index + 1, 0, "silent");
  };

  const handleImageToolbarClick = useCallback(() => {
    if (!onUploadImageRef.current) return;
    if (!fileInputRef.current) return;
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  }, []);

  const { addNotification } = useNotification();

  const processImageFile = async (file: File): Promise<File | null> => {
    if (
      file.type === "image/heic" ||
      file.type === "image/heif" ||
      file.name.toLowerCase().endsWith(".heic") ||
      file.name.toLowerCase().endsWith(".heif")
    ) {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const outputBuffer = await heicConvert({
          buffer: new Uint8Array(arrayBuffer),
          format: "JPEG",
          quality: 0.8,
        });
        return new File(
          [outputBuffer as Uint8Array],
          file.name.replace(/\.[^/.]+$/, ".jpg"),
          { type: "image/jpeg" }
        );
      } catch {
        addNotification(
          "We couldn't process the uploaded HEIC image.",
          "error"
        );
        return null;
      }
    }
    return file;
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    let file = e.target.files?.[0];
    const uploader = onUploadImageRef.current;
    if (!file || !uploader) return;

    try {
      onUploadStateChangeRef.current?.(true);
      const processed = await processImageFile(file);
      if (!processed) return;
      file = processed;
      const url = await uploader(file);
      if (url) {
        insertImageAtCursor(url);
      }
    } finally {
      onUploadStateChangeRef.current?.(false);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      const uploader = onUploadImageRef.current;
      if (!uploader) return;

      const files = Array.from(e.dataTransfer?.files || []);
      let imageFile = files.find(
        (file) =>
          file.type.startsWith("image/") ||
          file.name.toLowerCase().endsWith(".heic") ||
          file.name.toLowerCase().endsWith(".heif")
      );
      if (!imageFile) return;

      try {
        onUploadStateChangeRef.current?.(true);
        const processed = await processImageFile(imageFile);
        if (!processed) return;
        imageFile = processed;
        const url = await uploader(imageFile);
        if (url) {
          insertImageAtCursor(url);
        }
      } finally {
        onUploadStateChangeRef.current?.(false);
      }
    },
    [addNotification]
  );

  const insertDivider = useCallback(() => {
    if (!quillRef.current) return;
    const quill = quillRef.current.getEditor();
    const range = quill.getSelection(true);
    const index = range ? range.index : quill.getLength();
    quill.insertEmbed(index, "divider", true, "user");
    quill.insertText(index + 1, "\n", "user");
    quill.setSelection(index + 2, 0, "silent");
  }, []);

  const modules = React.useMemo(
    () => ({
      toolbar:
        hideToolbar || readOnly
          ? false
          : {
              container: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline"],
                ["blockquote"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link", "image"],
                ["divider"],
              ],
              handlers: {
                image: handleImageToolbarClick,
                divider: insertDivider,
              },
            },
    }),
    [hideToolbar, readOnly, handleImageToolbarClick, insertDivider]
  );

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "blockquote",
    "list",
    "bullet",
    "link",
    "image",
    "divider",
  ];

  const handleChange = React.useCallback(
    (content: string, delta: any, source: string) => {
      // Avoid feedback loops: ReactQuill will call onChange with source "api"
      // when it receives a new `value` prop. We only want to propagate user edits.
      if (source === "user") {
        onChange(content);
      }
    },
    [onChange]
  );

  if (toolbarOnly) {
    return null;
  }

  if (readOnly) {
    return (
      <EditorContainer>
        <div
          className="ql-container ql-snow"
          style={{ minHeight: "fit-content" }}
        >
          <div
            className="ql-editor"
            style={{ borderRadius: "10px", minHeight: "200px" }}
            dangerouslySetInnerHTML={{ __html: value || "" }}
          />
        </div>
      </EditorContainer>
    );
  }

  return (
    <EditorContainer onDragOver={handleDragOver} onDrop={handleDrop}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        style={{ display: "none" }}
        onChange={handleFileSelected}
      />
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={handleChange}
        placeholder={placeholder}
        modules={modules}
        formats={formats}
        readOnly={readOnly}
      />
    </EditorContainer>
  );
};

export default RichTextEditor;
